<template>
    <div class="wallet-modal-backdrop">
        <div class="modal-card" role="dialog" :aria-label="$t('userWallet.stake.title')">
            <div class="modal-header">
                <div class="modal-title-row">
                    <img src="/assets/images/wallets/aube.png" alt="" width="28" height="28">
                    <h2>{{ $t('userWallet.stake.title') }}</h2>
                </div>
                <button class="icon-button" type="button" :aria-label="$t('userWallet.close')" @click="close">
                    <img src="/assets/images/wallets/close.svg" alt="">
                </button>
            </div>

            <div class="modal-body" v-if="step === 'form'">
                <label class="field">
                    <span>{{ $t('userWallet.stake.amount') }}</span>
                    <span class="amount-field">
                        <input
                            id="stakingAmount"
                            type="text"
                            inputmode="decimal"
                            :value="form.amount"
                            @input="onAmountInput"
                            :placeholder="$t('userWallet.stake.amountPlaceholder')"
                            autocomplete="off"
                            :disabled="submitting"
                        >
                        <strong>AUBE</strong>
                    </span>
                </label>
                <p class="field-error" aria-live="polite">{{ amountError || '' }}</p>

                <fieldset class="staking-plan-fieldset">
                    <legend class="staking-plan-legend">{{ $t('userWallet.stake.planFieldLabel') }}</legend>
                    <div class="staking-plan-options">
                        <label v-for="plan in localizedPlans" :key="plan.value" class="staking-plan-option">
                            <input
                                type="radio"
                                name="stakingPlan"
                                :value="plan.value"
                                v-model="form.plan"
                                :disabled="submitting"
                            >
                            <span class="staking-plan-label">
                                <span class="staking-plan-duration">{{ plan.label }}</span>
                            </span>
                        </label>
                    </div>
                </fieldset>

                <div
                    v-if="estimatedGasEth !== null || gasLoading || gasEstimateError"
                    class="gas-estimate"
                    :class="{ 'gas-estimate--loading': gasLoading }"
                    aria-live="polite"
                    :aria-busy="gasLoading"
                >
                    <div>
                        <span>{{ $t('userWallet.stake.gasLabel') }}</span>
                        <span class="gas-estimate-value">
                            <span class="gas-estimate-spinner" :class="{ 'is-visible': gasLoading }" aria-hidden="true"></span>
                            <strong v-if="estimatedGasEth !== null">{{ formatAmount(estimatedGasEth) }} ETH</strong>
                        </span>
                    </div>
                    <p v-if="estimatedGasEth !== null" class="gas-estimate-note">
                        <span class="info-icon" aria-hidden="true">i</span>
                        {{ $t('userWallet.stake.gasNote') }}
                    </p>
                    <p v-if="gasQuoteExpired" class="field-error gas-quote-error" aria-live="polite">
                        {{ $t('userWallet.stake.gasExpired') }}
                    </p>
                    <button
                        v-if="gasQuoteExpired || gasEstimateError || insufficientGas"
                        class="quote-link-button"
                        type="button"
                        :disabled="gasLoading"
                        @click="requoteGas"
                    >
                        {{ $t('userWallet.stake.requote') }}
                    </button>
                </div>
                <p v-if="insufficientGas" class="field-error" aria-live="polite">
                    {{ $t('userWallet.errors.INSUFFICIENT_ETH_FOR_GAS') }}
                </p>

                <p class="helper-text">{{ $t('userWallet.stake.agreementsHelper') }}</p>
                <div class="policy-agreements">
                    <label class="policy-check-row">
                        <input type="checkbox" v-model="agreeTerms" :disabled="!termsUnlocked">
                        <span>
                            {{ $t('userWallet.stake.agreePrefix') }}<a
                                class="policy-link"
                                :href="stakingTermsUrl"
                                target="_blank"
                                rel="noopener noreferrer"
                                @click="onTermsLinkClick"
                            >{{ $t('userWallet.stake.termsLink') }}</a>{{ $t('userWallet.stake.agreeSuffix') }}
                        </span>
                    </label>
                    <label class="policy-check-row">
                        <input type="checkbox" v-model="agreePrivacy" :disabled="!privacyUnlocked">
                        <span>
                            {{ $t('userWallet.stake.agreePrefix') }}<a
                                class="policy-link"
                                :href="privacyUrl"
                                target="_blank"
                                rel="noopener noreferrer"
                                @click="onPrivacyLinkClick"
                            >{{ $t('userWallet.stake.privacyLink') }}</a>{{ $t('userWallet.stake.agreeSuffix') }}
                        </span>
                    </label>
                </div>

                <p class="field-error" aria-live="polite">{{ error || '' }}</p>
            </div>

            <div class="modal-body" v-else-if="step === 'confirm'" aria-label="stake confirmation">
                <div class="stake-plan-summary">
                    <div>
                        <span>{{ $t('userWallet.stake.confirmAmountLabel') }}</span>
                        <strong>{{ confirmAmountText }}</strong>
                    </div>
                    <div>
                        <span>{{ $t('userWallet.stake.plan') }}</span>
                        <strong>{{ selectedPlanLabel }}</strong>
                    </div>
                </div>

                <p class="modal-lead">{{ $t('userWallet.stake.confirmLead') }}</p>
                <ul class="important-matters-list">
                    <li>
                        <label class="policy-check-row">
                            <input type="checkbox" v-model="agreeMatter1">
                            <span>{{ $t('userWallet.stake.matter1') }}</span>
                        </label>
                    </li>
                    <li>
                        <label class="policy-check-row">
                            <input type="checkbox" v-model="agreeMatter2">
                            <span>{{ $t('userWallet.stake.matter2') }}</span>
                        </label>
                    </li>
                    <li>
                        <label class="policy-check-row">
                            <input type="checkbox" v-model="agreeMatter3">
                            <span>{{ $t('userWallet.stake.matter3') }}</span>
                        </label>
                    </li>
                    <li>
                        <label class="policy-check-row">
                            <input type="checkbox" v-model="agreeMatter4">
                            <span>{{ $t('userWallet.stake.matter4') }}</span>
                        </label>
                    </li>
                </ul>

                <p class="field-error" aria-live="polite">{{ error || '' }}</p>
            </div>

            <div class="modal-body" v-else aria-label="stake accepted">
                <div class="completion-mark">
                    <svg aria-hidden="true" viewBox="0 0 24 24">
                        <path d="M20 6 9 17l-5-5"></path>
                    </svg>
                </div>
                <p class="completion-title">{{ $t('userWallet.stake.accepted') }}</p>
            </div>

            <div class="modal-actions">
                <button
                    v-if="step === 'confirm'"
                    class="secondary-button"
                    type="button"
                    :disabled="submitting"
                    @click="backToForm"
                >{{ $t('userWallet.stake.back') }}</button>

                <button
                    v-if="step === 'form'"
                    class="primary-button"
                    type="button"
                    :disabled="!canProceedToConfirm || submitting"
                    @click="proceedToConfirm"
                >{{ $t('userWallet.stake.proceedToConfirm') }}</button>
                <button
                    v-if="step === 'confirm'"
                    class="primary-button"
                    type="button"
                    :disabled="!canSubmit || submitting"
                    @click="submit"
                >{{ submitting ? $t('userWallet.stake.submitting') : $t('userWallet.stake.submit') }}</button>
            </div>
        </div>
    </div>
</template>

<script>
    import { v4 as uuidv4 } from 'uuid';
    import UserWalletService from '../../../services/UserWalletService';
    import walletFormatMixin from '../../../utils/walletFormatMixin';

    const MIN_STAKE_AMOUNT = 0.0001;

    // 見積りの有効期限（2分）。UIテスト時は短い値に変更して確認可能。
    const GAS_QUOTE_TTL_MS = 2 * 60 * 1000;

    // Per-attempt reference_uuid, generated here (not by the backend) and kept in sessionStorage
    // alongside the amount/plan it was generated for, so a reload/retry of the SAME attempt
    // reuses it (doubling as the client-retry idempotency key), while a changed amount/plan (a
    // different attempt) gets a fresh one. Also becomes blockchain-service's reference_id once
    // the Outbox job submits it — see AubeStakeRequestService::createRequest().
    const REFERENCE_UUID_STORAGE_KEY = 'aubeStakeReferenceUuid';

    export default {
        name: 'WalletStakeModal',

        mixins: [walletFormatMixin],

        props: {
            availableBalance: {type: String, required: true},
            plans: {type: Array, required: true},
        },

        data() {
            return {
                form: {
                    amount: '',
                    plan: this.plans.length > 0 ? this.plans[0].value : '',
                },
                step: 'form', // 'form' | 'confirm' | 'done'
                submitting: false,
                referenceUuid: null,
                error: null,
                amountError: null,
                estimatedGasEth: null,
                gasLoading: false,
                gasQuoteExpired: false,
                gasQuoteTimerId: null,
                gasEstimateError: false,
                // 見積り自体は成功しているがガス代が払えないケース。見積り額は表示したまま
                // エラー文言を出して submit を止める（何ETH足りないか分からないと困るため）。
                insufficientGas: false,

                agreeTerms: false,
                agreePrivacy: false,
                termsUnlocked: false,
                privacyUnlocked: false,
                pendingUnlock: null, // 'terms' | 'privacy' | null
                stakingTermsUrl: '',
                privacyUrl: '',

                agreeMatter1: false,
                agreeMatter2: false,
                agreeMatter3: false,
                agreeMatter4: false,
            };
        },

        watch: {
            'form.amount'() {
                this.amountError = null;
            },
        },

        created() {
            this.fetchEstimatedGas();
            // モバイル実機(iOS Safari/Android Chrome)では target="_blank" で開いたタブから
            // 戻っても window の 'focus' が発火しないことがあるため、Page Visibility API も
            // 併用する(handleWindowFocus は pendingUnlock が無ければ何もしないので二重発火は無害)。
            window.addEventListener('focus', this.handleWindowFocus);
            document.addEventListener('visibilitychange', this.handleVisibilityChange);

            const resolvedPrivacy = this.$router.resolve({name: 'policy'});
            this.privacyUrl = resolvedPrivacy && resolvedPrivacy.href ? resolvedPrivacy.href : '/policy';

            const resolvedTerms = this.$router.resolve({name: 'aubeStakeTerms'});
            this.stakingTermsUrl = resolvedTerms && resolvedTerms.href ? resolvedTerms.href : '#';
        },

        beforeDestroy() {
            this.clearGasQuoteTimer();
            window.removeEventListener('focus', this.handleWindowFocus);
            document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        },

        computed: {
            // plan.label はサーバー側で解決された言語（app()->getLocale()）に依存し、
            // 画面表示中の言語（this.$i18n.locale）と一致しない場合があるため、
            // plan.labels（{ja, en, ...}）から表示言語のラベルを解決する。
            localizedPlans() {
                const shortLocale = (this.$i18n.locale || '').slice(0, 2);
                return this.plans.map((plan) => ({
                    ...plan,
                    label: (plan.labels && plan.labels[shortLocale]) || plan.label,
                }));
            },

            selectedPlanLabel() {
                const plan = this.localizedPlans.find((p) => p.value === this.form.plan);
                return plan ? plan.label : '';
            },

            confirmAmountText() {
                return `${this.formatAmount(this.form.amount)} AUBE`;
            },

            canProceedToConfirm() {
                return !!this.form.plan
                    && this.estimatedGasEth !== null
                    && !this.gasEstimateError
                    && !this.insufficientGas
                    && !this.gasQuoteExpired
                    && this.agreeTerms
                    && this.agreePrivacy;
            },

            canSubmit() {
                return this.estimatedGasEth !== null
                    && !this.gasEstimateError
                    && !this.gasQuoteExpired
                    && this.agreeMatter1
                    && this.agreeMatter2
                    && this.agreeMatter3
                    && this.agreeMatter4;
            },
        },

        methods: {
            onAmountInput(event) {
                const raw = event.target.value;
                // iOS の inputmode="decimal" キーボードは端末の地域設定によって
                // '.' ではなく ',' キーしか出さないことがあるため、',' も小数点として受け付ける。
                let sanitized = raw.replace(/,/g, '.').replace(/[^0-9.]/g, '');
                const firstDotIndex = sanitized.indexOf('.');
                if (firstDotIndex !== -1) {
                    sanitized = sanitized.slice(0, firstDotIndex + 1)
                        + sanitized.slice(firstDotIndex + 1).replace(/\./g, '');
                }

                this.form.amount = sanitized;
                if (raw !== sanitized) {
                    event.target.value = sanitized;
                }
            },

            async fetchEstimatedGas() {
                if (this.gasLoading) {
                    return;
                }

                this.gasLoading = true;
                try {
                    const response = await UserWalletService.getStakeEstimatedGas();
                    this.estimatedGasEth = response.data.estimated_gas_fee;
                    this.insufficientGas = response.data.insufficient_gas === true;
                    this.gasEstimateError = false;
                    this.error = null;
                    this.startGasQuoteTimer();
                } catch (e) {
                    // 見積り失敗時は古い値を信用せず破棄し、submit失敗時と同じ下部エラー欄 + 再見積り導線を出す
                    this.clearGasQuoteTimer();
                    this.estimatedGasEth = null;
                    this.gasQuoteExpired = false;
                    this.insufficientGas = false;
                    this.gasEstimateError = true;
                    this.error = this.$t('userWallet.errors.stakeFailed');
                } finally {
                    this.gasLoading = false;
                }
            },

            requoteGas() {
                if (this.gasLoading) {
                    return;
                }
                this.fetchEstimatedGas();
            },

            clearGasQuoteTimer() {
                if (this.gasQuoteTimerId) {
                    clearTimeout(this.gasQuoteTimerId);
                    this.gasQuoteTimerId = null;
                }
            },

            startGasQuoteTimer() {
                this.clearGasQuoteTimer();
                this.gasQuoteExpired = false;
                this.gasQuoteTimerId = setTimeout(() => {
                    this.gasQuoteTimerId = null;
                    this.gasQuoteExpired = true;
                    // 確認ステップで見積り切れした場合は入力ステップへ戻す(再見積り必須)
                    if (this.step === 'confirm') {
                        this.step = 'form';
                    }
                }, GAS_QUOTE_TTL_MS);
            },

            onTermsLinkClick() {
                this.pendingUnlock = 'terms';
            },

            onPrivacyLinkClick() {
                this.pendingUnlock = 'privacy';
            },

            handleWindowFocus() {
                if (!this.pendingUnlock) {
                    return;
                }
                if (this.pendingUnlock === 'terms') {
                    this.termsUnlocked = true;
                } else if (this.pendingUnlock === 'privacy') {
                    this.privacyUnlocked = true;
                }
                this.pendingUnlock = null;
            },

            handleVisibilityChange() {
                if (document.visibilityState === 'visible') {
                    this.handleWindowFocus();
                }
            },

            close() {
                if (this.submitting) {
                    return;
                }
                this.$emit('close');
            },

            validateAmount() {
                this.amountError = null;

                if (!/^\d{1,18}(\.\d{1,8})?$/.test(this.form.amount)) {
                    this.amountError = this.$t('userWallet.errors.INVALID_AMOUNT');
                    return false;
                }

                if (parseFloat(this.form.amount) < MIN_STAKE_AMOUNT) {
                    this.amountError = this.$t('userWallet.errors.belowMinAmount');
                    return false;
                }

                if (parseFloat(this.form.amount) > parseFloat(this.availableBalance)) {
                    this.amountError = this.$t('userWallet.errors.insufficientBalance');
                    return false;
                }

                return true;
            },

            proceedToConfirm() {
                this.error = null;
                if (!this.canProceedToConfirm) {
                    return;
                }
                if (!this.validateAmount()) {
                    this.scrollErrorIntoView();
                    return;
                }
                this.referenceUuid = this.resolveReferenceUuid();
                this.step = 'confirm';
            },

            /**
             * Reuses the stored value only when it was generated for this exact amount+plan (a
             * retry of the same attempt); a changed amount/plan is a different attempt and gets a
             * fresh one. sessionStorage failures (private mode, disabled storage) fall back to an
             * in-memory-only value — idempotency still works within this page load, it just can't
             * survive a reload, which matches today's behavior anyway.
             */
            resolveReferenceUuid() {
                try {
                    const raw = sessionStorage.getItem(REFERENCE_UUID_STORAGE_KEY);
                    if (raw) {
                        const stored = JSON.parse(raw);
                        if (stored && stored.value && stored.amount === this.form.amount && stored.plan === this.form.plan) {
                            return stored.value;
                        }
                    }
                } catch (e) {
                    // sessionStorage unavailable or corrupt — fall through to generating a fresh value.
                }

                // uuid's v4() always produces a UUIDv4 (matches AppCreateAubeStakeRequest's
                // regex) regardless of browser/HTTPS support, unlike relying on the native
                // crypto.randomUUID() (needs a secure context + fairly recent browser) with a
                // hand-rolled fallback — that fallback previously produced a non-UUID-shaped
                // string that the backend's regex would have rejected outright.
                const value = uuidv4();

                try {
                    sessionStorage.setItem(REFERENCE_UUID_STORAGE_KEY, JSON.stringify({
                        value,
                        amount: this.form.amount,
                        plan: this.form.plan,
                    }));
                } catch (e) {
                    // Ignore — value still works for this page load via this.referenceUuid.
                }

                return value;
            },

            clearReferenceUuid() {
                try {
                    sessionStorage.removeItem(REFERENCE_UUID_STORAGE_KEY);
                } catch (e) {
                    // Ignore.
                }
            },

            // 画面が小さいとフォームが縦に長く、下の方(ボタン付近)までスクロールした状態で
            // バリデーションに失敗すると、上部の入力エラーが画面外になり気づけない。
            // 最初に見つかった非空のエラーメッセージまでスクロールして見せる。
            async scrollErrorIntoView() {
                await this.$nextTick();
                const errorEls = this.$el.querySelectorAll('.field-error');
                for (const el of errorEls) {
                    if (el.textContent && el.textContent.trim()) {
                        el.scrollIntoView({behavior: 'smooth', block: 'center'});
                        break;
                    }
                }
            },

            backToForm() {
                this.error = null;
                this.step = 'form';
            },

            async submit() {
                this.error = null;

                if (this.estimatedGasEth === null || this.gasEstimateError || this.gasQuoteExpired) {
                    this.step = 'form';
                    return;
                }

                if (!this.validateAmount() || this.insufficientGas) {
                    this.step = 'form';
                    this.scrollErrorIntoView();
                    return;
                }

                this.submitting = true;
                try {
                    await UserWalletService.createStakeRequest({
                        amount: this.form.amount,
                        plan: this.form.plan,
                        reference_uuid: this.referenceUuid,
                    });
                    // Accepted (or an idempotent replay of an already-accepted attempt) — this
                    // attempt's fate is settled, a future retry must not reuse this value.
                    this.clearReferenceUuid();
                    this.clearGasQuoteTimer();
                    this.step = 'done';
                    this.$emit('accepted');
                } catch (e) {
                    if (e.response) {
                        // Server actually answered (validation/conflict/business error) — the
                        // outcome is determined, so this value must not be reused either.
                        this.clearReferenceUuid();
                    }
                    // No e.response (network error, timeout, connection drop): outcome unknown —
                    // keep the value so a retry is recognized server-side as the same attempt
                    // instead of creating a second real stake request.

                    const code = e.response && e.response.data && e.response.data.error
                        ? e.response.data.error.code
                        : null;
                    const key = 'userWallet.errors.' + code;
                    this.error = code && this.$te(key)
                        ? this.$t(key)
                        : this.$t('userWallet.errors.stakeFailed');

                    // submit() は confirm ステップからのみ呼ばれる。エラー発生時に form ステップへ
                    // 戻すと、そのエラーが起きたステップとは別の場所にメッセージが表示されてしまうため、
                    // どのエラーコードでも confirm ステップのまま維持し、その場でエラー表示 + 再試行できるようにする。
                    // (INSUFFICIENT_ETH_FOR_GAS は通常 gas-estimate の insufficient_gas で
                    // form ステップに出るが、見積り後に ETH が他の送付でロックされた場合は
                    // submit まで到達しうる)
                    this.scrollErrorIntoView();
                } finally {
                    this.submitting = false;
                }
            },
        },
    };
</script>
